/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable arrow-body-style */
import { ResultsStatus } from '@src/common/types';
import cytoscape, { Core, LayoutOptions } from 'cytoscape';
import klay from 'cytoscape-klay';
import { create } from 'zustand';
import { eventSequenceManager } from '@src/common/util/eventSequenceManager';
import { wait } from '@src/common/util/wait';
import { getDebounce } from '@src/common/util/getDebounce';
import { laFetchGraph } from '../api/laFetchGraph';
import { LaFilterPanelState, LinkAnalysisResultsStore } from '../types';
import { applyGrabState } from './applyGrabState';
import { cytoscapeStyle } from './cytoscapeStyle';
import { getCytoscapeElementsFromGraphData } from './getCytoscapeElementsFromGraphData';
import { getLegend } from './getLegend';
import { downloadGraphImage } from './download/downloadGraphImage';
import { downloadGraphToast } from './download/downloadGraphToast';
import { downloadGraphCsv } from './download/downloadGraphCsv';
import { laFetchEdges } from '../api/laFetchEdges';
import { laFilterPanelChangeManager } from './laFilterPanelChangeManager';
import { graphNodeHoverApi } from './cyNodeHover';
import { MAX_EDGES_FOR_EXPORT } from '../api/edgeLimits';
import { cyHighlightPaths } from './cyPathHighlighting/cyHighlightPaths';

cytoscape.use(klay);

const klayLayout: LayoutOptions = {
  name: 'klay',
  randomize: false,
  animate: true,
  avoidOverlap: true,
  fit: true,
  nodeDimensionsIncludeLabels: true,
  randomSeed: 45,
};

export const useLinkAnalysisResultsStore = create<LinkAnalysisResultsStore>(
  () => {
    return {
      resultStatus: ResultsStatus.pendingSearch,
      sourceNodeId: '',
      graphElements: [],
      graphDataQeJson: JSON.stringify([]),
      legend: [],
      fpStateApplied: {
        idType: '',
        value: '',
        nodesValue: [],
        hop: '',
        maxIpConnections: '',
      },
      csvExportInProgress: false,
      searchFieldValue: '',
      qeHighlightedPaths: '',
    };
  }
);
const store = useLinkAnalysisResultsStore;

export const linkAnalysisResultsApi = api();

function api() {
  let graphContainer: HTMLDivElement | null = null;
  const raceConditionDetector = eventSequenceManager();
  let cy: Core;
  let searchValue = '';
  const graphSearchDebouncer = getDebounce();

  return {
    search,
    setGraphContainer,
    downloadImg,
    downloadCsv,
    setSearchValue,
    clearSearchValue,
  };

  async function search(fpState: LaFilterPanelState) {
    const snapshot = raceConditionDetector.queueEvent();
    searchValue = fpState.value;
    store.setState({
      resultStatus: ResultsStatus.searching,
      graphElements: [],
      graphDataQeJson: JSON.stringify([]),
      legend: [],
      fpStateApplied: { ...fpState },
      searchFieldValue: '',
    });
    const { result, resultStatus } = await laFetchGraph(fpState);
    if (snapshot.isStale()) {
      return;
    }

    const graphElements = getCytoscapeElementsFromGraphData(result);
    const graphDataQeJson = JSON.stringify(graphElements);
    const legend = getLegend(result.nodes);
    store.setState({
      resultStatus,
      graphElements,
      sourceNodeId: result.sourceNodeId,
      graphDataQeJson,
      legend,
    });
    renderCy();
  }

  async function downloadImg() {
    laFilterPanelChangeManager.setSubmitBlocked(true);
    await downloadGraphImage(cy, searchValue);
    laFilterPanelChangeManager.setSubmitBlocked(false);
  }

  async function downloadCsv() {
    laFilterPanelChangeManager.setSubmitBlocked(true);
    downloadGraphToast.showCsvToast(ResultsStatus.results);
    useLinkAnalysisResultsStore.setState({ csvExportInProgress: true });
    const { fpStateApplied } = useLinkAnalysisResultsStore.getState();

    // allow the menu to close and the toast to show before the intense processing begins
    await wait(400);

    const { resultStatus, edges } = await laFetchEdges(
      fpStateApplied,
      MAX_EDGES_FOR_EXPORT
    );

    if (resultStatus === ResultsStatus.error) {
      downloadGraphToast.showCsvToast(ResultsStatus.error);
      useLinkAnalysisResultsStore.setState({ csvExportInProgress: false });
      laFilterPanelChangeManager.setSubmitBlocked(false);
      return false;
    }

    await downloadGraphCsv(searchValue, edges);
    useLinkAnalysisResultsStore.setState({ csvExportInProgress: false });
    laFilterPanelChangeManager.setSubmitBlocked(false);
    return true;
  }

  function setGraphContainer(container: HTMLDivElement) {
    graphContainer = container;
    renderCy();
  }

  function renderCy() {
    if (!graphContainer) {
      return;
    }

    console.time('cytoscape-render');
    cy = cytoscape({
      container: graphContainer,
      elements: store.getState().graphElements,
      style: cytoscapeStyle,
      // maxZoom: 4,
      // minZoom: 0.2,
      // wheelSensitivity: 0.4,
      layout: klayLayout,
    });
    cy.ready(() => {
      console.timeEnd('cytoscape-render');
      applyGrabState(cy, graphContainer!);
      graphNodeHoverApi.applyCy(cy, graphContainer!);
      cyHighlightPaths.init({
        cy,
        sourceNodeId: store.getState().sourceNodeId,
        setHighlightedPaths: (paths: string) =>
          store.setState({ qeHighlightedPaths: paths }),
      });
    });
  }

  function setSearchValue(e: React.ChangeEvent<HTMLInputElement>) {
    store.setState({ searchFieldValue: e.target.value });
    graphSearchDebouncer(() => {
      cyHighlightPaths.highlightPaths({
        cy,
        searchValue: e.target.value,
      });
    }, 400);
  }
  function clearSearchValue() {
    store.setState({ searchFieldValue: '' });
    cyHighlightPaths.clear(cy);
  }
}
